// Shared in-browser video render pipeline (used by the background render manager).
// Pure functions — no React. They operate on detached <video>/<canvas> elements
// so renders keep running even after their originating modal closes.
import {
  buildGroups, drawCaptionFrame, drawVideoContain, getPreset,
} from './captionPresets';
import {
  loadImage, drawLogo, drawProgressBar, drawLowerThird, drawOutro,
} from './brandingOverlay';

export const FORMAT_DIMS = { '9:16': [1080, 1920], '16:9': [1920, 1080], '1:1': [1080, 1080], '4:5': [1080, 1350] };

let _ff = null;
export async function loadFfmpeg() {
  if (_ff) return _ff;
  const { FFmpeg } = await import('@ffmpeg/ffmpeg');
  const { toBlobURL } = await import('@ffmpeg/util');
  const ff = new FFmpeg();
  const base = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
  await ff.load({
    coreURL: await toBlobURL(`${base}/ffmpeg-core.js`, 'text/javascript'),
    wasmURL: await toBlobURL(`${base}/ffmpeg-core.wasm`, 'application/wasm'),
  });
  _ff = ff;
  return ff;
}

export async function extractAudio(srcBlob, { trimStart = 0, trimEnd = 0, clipDuration = 0 } = {}) {
  const { fetchFile } = await import('@ffmpeg/util');
  const ff = await loadFfmpeg();
  await ff.writeFile('aud_in', await fetchFile(srcBlob));
  const args = ['-i', 'aud_in'];
  if (trimStart > 0) args.push('-ss', String(trimStart.toFixed(2)));
  if (trimEnd && clipDuration && trimEnd < clipDuration) args.push('-to', String(trimEnd.toFixed(2)));
  args.push('-vn', '-ac', '1', '-c:a', 'aac', '-b:a', '64k', 'aud_out.m4a');
  await ff.exec(args);
  const data = await ff.readFile('aud_out.m4a');
  return new Blob([data.buffer], { type: 'audio/mp4' });
}

export async function transcodeToMp4(webmBlob) {
  const { fetchFile } = await import('@ffmpeg/util');
  const ff = await loadFfmpeg();
  await ff.writeFile('vr_in.webm', await fetchFile(webmBlob));
  // TikTok-safe MP4: H.264 high@4.1, yuv420p, +faststart, AAC 44.1k, 30fps.
  await ff.exec([
    '-i', 'vr_in.webm',
    '-c:v', 'libx264', '-preset', 'veryfast', '-profile:v', 'high', '-level', '4.1',
    '-pix_fmt', 'yuv420p', '-r', '30', '-movflags', '+faststart',
    '-c:a', 'aac', '-b:a', '128k', '-ar', '44100',
    'vr_out.mp4',
  ]);
  const data = await ff.readFile('vr_out.mp4');
  return new Blob([data.buffer], { type: 'video/mp4' });
}

// Transcode any input to VP8/Vorbis WebM — decodable by every Chromium build
// (including headless, which lacks proprietary H.264). Used as a source
// fallback so canvas compositing can decode phone-recorded MP4s reliably.
export async function transcodeToWebm(srcBlob) {
  const { fetchFile } = await import('@ffmpeg/util');
  const ff = await loadFfmpeg();
  await ff.writeFile('tw_in', await fetchFile(srcBlob));
  await ff.exec(['-i', 'tw_in', '-c:v', 'libvpx', '-b:v', '2M', '-deadline', 'realtime', '-cpu-used', '5', '-pix_fmt', 'yuv420p', '-c:a', 'libvorbis', 'tw_out.webm']);
  const data = await ff.readFile('tw_out.webm');
  return new Blob([data.buffer], { type: 'video/webm' });
}

export async function exportPlain({ srcBlob, format, trimStart = 0, trimEnd = 0, clipDuration = 0 }) {
  const { fetchFile } = await import('@ffmpeg/util');
  const ff = await loadFfmpeg();
  await ff.writeFile('ep_in.webm', await fetchFile(srcBlob));
  const [w, h] = FORMAT_DIMS[format];
  const args = ['-i', 'ep_in.webm'];
  if (trimStart > 0) args.push('-ss', String(trimStart.toFixed(2)));
  if (trimEnd && clipDuration && trimEnd < clipDuration) args.push('-to', String(trimEnd.toFixed(2)));
  args.push('-vf', `scale=${w}:${h}:force_original_aspect_ratio=decrease,pad=${w}:${h}:(ow-iw)/2:(oh-ih)/2:black`,
    '-c:v', 'libx264', '-preset', 'veryfast', '-profile:v', 'high', '-level', '4.1', '-pix_fmt', 'yuv420p',
    '-r', '30', '-movflags', '+faststart', '-c:a', 'aac', '-b:a', '128k', '-ar', '44100', 'ep_out.mp4');
  await ff.exec(args);
  const data = await ff.readFile('ep_out.mp4');
  return new Blob([data.buffer], { type: 'video/mp4' });
}

// Prefer WebM (Chrome/Edge). Safari/iOS can't record WebM but supports MP4 —
// falling back to MP4 lets caption burn-in (canvas.captureStream + MediaRecorder)
// work on those devices too. ffmpeg auto-detects the container on transcode.
const mimeForRecorder = () => {
  const candidates = [
    'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm',
    'video/mp4;codecs=h264,aac', 'video/mp4',
  ];
  for (const c of candidates) {
    try { if (MediaRecorder.isTypeSupported(c)) return c; } catch (_) { /* ignore */ }
  }
  return 'video/webm';
};

export async function renderCaptioned({ srcBlob, words, presetKey, format, trimStart = 0, trimEnd = 0, clipDuration = 0 }) {
  const [W, H] = FORMAT_DIMS[format];
  const url = URL.createObjectURL(srcBlob);
  const video = document.createElement('video');
  video.src = url; video.playsInline = true;
  await new Promise((res, rej) => { video.onloadeddata = res; video.onerror = () => rej(new Error('load')); });

  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  const preset = getPreset(presetKey);
  const groups = buildGroups(words, preset.maxWords);

  const audioCtx = new AudioContext();
  const srcNode = audioCtx.createMediaElementSource(video);
  const dest = audioCtx.createMediaStreamDestination();
  srcNode.connect(dest);

  const canvasStream = canvas.captureStream(30);
  const mixed = new MediaStream([canvasStream.getVideoTracks()[0], ...dest.stream.getAudioTracks()]);
  const rec = new MediaRecorder(mixed, { mimeType: mimeForRecorder(), videoBitsPerSecond: 6_000_000 });
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };

  const start = trimStart || 0;
  const end = (trimEnd && trimEnd > start) ? trimEnd : (clipDuration || video.duration || 0);
  await new Promise((res) => { video.currentTime = start; video.onseeked = res; });
  const done = new Promise((res) => { rec.onstop = res; });
  rec.start(100);
  await video.play();
  const draw = () => {
    const t = video.currentTime;
    if (video.paused || video.ended || (end && t >= end)) {
      if (rec.state !== 'inactive') rec.stop();
      video.pause();
      return;
    }
    drawVideoContain(ctx, video, W, H);
    drawCaptionFrame(ctx, groups, t, preset, W, H);
    requestAnimationFrame(draw);
  };
  requestAnimationFrame(draw);
  await done;
  try { await audioCtx.close(); } catch (_) { /* noop */ }
  URL.revokeObjectURL(url);
  return new Blob(chunks, { type: 'video/webm' });
}

export async function renderTimeline({ clips, broll = [], format, onProgress }) {
  const [W, H] = FORMAT_DIMS[format];
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  const audioCtx = new AudioContext();
  const audioDest = audioCtx.createMediaStreamDestination();
  const canvasStream = canvas.captureStream(30);
  const mixed = new MediaStream([canvasStream.getVideoTracks()[0], ...audioDest.stream.getAudioTracks()]);
  const rec = new MediaRecorder(mixed, { mimeType: mimeForRecorder(), videoBitsPerSecond: 6_000_000 });
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  const done = new Promise((res) => { rec.onstop = res; });

  // Preload B-roll overlay clips (video-only, A-roll audio keeps playing = cutaway).
  const brolls = await Promise.all((broll || []).map(async (b) => {
    const v = document.createElement('video');
    v.src = b.url; v.playsInline = true; v.muted = true; v.crossOrigin = 'anonymous';
    await new Promise((res) => { v.onloadeddata = res; v.onerror = res; });
    const start = b.start || 0;
    const len = Math.max(0.1, (b.trimEnd || b.duration || 0) - (b.trimStart || 0));
    return { v, start, end: start + len, trimStart: b.trimStart || 0 };
  }));

  rec.start(100);
  let timeline = 0; // global assembled-timeline clock (seconds)
  let active = null;

  for (let i = 0; i < clips.length; i++) {
    const clip = clips[i];
    onProgress?.(`Rendering clip ${i + 1}/${clips.length}…`);
    const v = document.createElement('video');
    v.src = clip.url; v.playsInline = true; v.crossOrigin = 'anonymous';
    // eslint-disable-next-line no-await-in-loop
    await new Promise((res, rej) => { v.onloadeddata = res; v.onerror = () => rej(new Error('load')); });
    let node = null;
    try { node = audioCtx.createMediaElementSource(v); node.connect(audioDest); } catch (_) { node = null; }
    const start = clip.trimStart || 0;
    const end = (clip.trimEnd && clip.trimEnd > start) ? clip.trimEnd : (clip.duration || v.duration || 0);
    const base = timeline;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((res) => { v.currentTime = start; v.onseeked = res; });
    // eslint-disable-next-line no-await-in-loop
    await v.play().catch(() => {});
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      const draw = () => {
        const ct = v.currentTime;
        if (v.ended || (end && ct >= end)) { v.pause(); timeline = base + (end - start); resolve(); return; }
        timeline = base + (ct - start);
        const want = brolls.find((b) => timeline >= b.start && timeline < b.end) || null;
        if (want !== active) {
          if (active) active.v.pause();
          active = want;
          if (active) {
            active.v.currentTime = active.trimStart + Math.max(0, timeline - active.start);
            active.v.play().catch(() => {});
          }
        }
        drawVideoContain(ctx, active ? active.v : v, W, H);
        requestAnimationFrame(draw);
      };
      requestAnimationFrame(draw);
    });
    if (node) { try { node.disconnect(); } catch (_) { /* noop */ } }
  }
  if (active) { try { active.v.pause(); } catch (_) { /* noop */ } }
  if (rec.state !== 'inactive') rec.stop();
  await done;
  try { await audioCtx.close(); } catch (_) { /* noop */ }
  return new Blob(chunks, { type: 'video/webm' });
}


async function loadAudioEl(url) {
  const a = document.createElement('audio');
  a.src = url; a.crossOrigin = 'anonymous'; a.preload = 'auto';
  await new Promise((res) => { a.oncanplaythrough = res; a.oncanplay = res; a.onerror = res; });
  return a;
}

// Returns a loaded, decodable <video>. Tries the direct URL, then the blob;
// if the browser can't decode the source codec (e.g. some phone MP4s), it
// transcodes to H.264 via ffmpeg and retries so canvas compositing can proceed.
async function makeSourceVideo(srcBlob, srcUrl, onProgress) {
  const tryLoad = (src) => new Promise((resolve) => {
    const v = document.createElement('video');
    v.playsInline = true;
    let settled = false;
    const finish = (val) => { if (!settled) { settled = true; resolve(val); } };
    v.onloadeddata = () => finish(v);
    v.onerror = () => finish(null);
    setTimeout(() => finish(null), 8000);
    v.src = src;
  });
  if (srcUrl) { const v = await tryLoad(srcUrl); if (v) return { video: v, cleanup: () => {} }; }
  let objUrl = URL.createObjectURL(srcBlob);
  let v = await tryLoad(objUrl);
  if (v) return { video: v, cleanup: () => URL.revokeObjectURL(objUrl) };
  URL.revokeObjectURL(objUrl);
  // Codec not decodable by this browser (e.g. some phone MP4s / headless H.264).
  // Transcode to VP8 WebM first (universally decodable), then H.264 MP4.
  onProgress?.('Converting source…');
  for (const make of [transcodeToWebm, transcodeToMp4]) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const out = await make(srcBlob);
      objUrl = URL.createObjectURL(out);
      // eslint-disable-next-line no-await-in-loop
      v = await tryLoad(objUrl);
      if (v) return { video: v, cleanup: () => URL.revokeObjectURL(objUrl) };
      URL.revokeObjectURL(objUrl);
    } catch (_) { /* try next codec */ }
  }
  throw new Error('Could not decode the source video');
}

// Phase 4 unified renderer: video + optional captions + branding/graphics
// overlays (logo, lower-third, progress bar, outro card) + optional background
// music and AI voiceover mixed into the audio. Everything is composited in the
// browser via canvas + Web Audio (no server compute).
export async function renderComposited({
  srcBlob, srcUrl = null, words = null, presetKey, format, trimStart = 0, trimEnd = 0, clipDuration = 0,
  overlays = {}, musicUrl = null, musicVolume = 0.2, duckMain = false,
  voiceoverUrl = null, onProgress,
}) {
  const [W, H] = FORMAT_DIMS[format];
  onProgress?.('Preparing video…');
  const { video, cleanup } = await makeSourceVideo(srcBlob, srcUrl, onProgress);
  video.playsInline = true;

  onProgress?.('Preparing assets…');
  const logoImg = overlays.logo?.enabled && overlays.logo?.url ? await loadImage(overlays.logo.url).catch(() => null) : null;
  const outroImg = overlays.outro?.enabled && overlays.outro?.mode === 'image' && overlays.outro?.url
    ? await loadImage(overlays.outro.url).catch(() => null) : null;

  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  const preset = words?.length ? getPreset(presetKey) : null;
  const groups = words?.length ? buildGroups(words, preset.maxWords) : null;

  const audioCtx = new AudioContext();
  try { await audioCtx.resume(); } catch (_) { /* noop */ }
  const dest = audioCtx.createMediaStreamDestination();
  const mainNode = audioCtx.createMediaElementSource(video);
  const mainGain = audioCtx.createGain();
  mainGain.gain.value = duckMain ? 0.15 : 1;
  mainNode.connect(mainGain).connect(dest);

  let musicEl = null;
  if (musicUrl) {
    musicEl = await loadAudioEl(musicUrl); musicEl.loop = true;
    const mn = audioCtx.createMediaElementSource(musicEl);
    const mg = audioCtx.createGain(); mg.gain.value = musicVolume ?? 0.2;
    mn.connect(mg).connect(dest);
  }
  let voEl = null;
  if (voiceoverUrl) {
    voEl = await loadAudioEl(voiceoverUrl);
    const vn = audioCtx.createMediaElementSource(voEl);
    const vg = audioCtx.createGain(); vg.gain.value = 1;
    vn.connect(vg).connect(dest);
  }

  const canvasStream = canvas.captureStream(30);
  const mixed = new MediaStream([canvasStream.getVideoTracks()[0], ...dest.stream.getAudioTracks()]);
  const rec = new MediaRecorder(mixed, { mimeType: mimeForRecorder(), videoBitsPerSecond: 6_000_000 });
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  const done = new Promise((res) => { rec.onstop = res; });

  const start = trimStart || 0;
  const end = (trimEnd && trimEnd > start) ? trimEnd : (clipDuration || video.duration || 0);
  const mainDur = Math.max(0.1, end - start);
  const outroDur = overlays.outro?.enabled ? (overlays.outro.duration || 3) : 0;
  await new Promise((res) => { video.currentTime = start; video.onseeked = res; });

  onProgress?.('Rendering…');
  rec.start(100);
  if (musicEl) musicEl.play().catch(() => {});
  if (voEl) voEl.play().catch(() => {});
  await video.play();

  let phase = 'main';
  let outroStartTs = 0;
  const finish = () => {
    if (rec.state !== 'inactive') rec.stop();
    try { video.pause(); } catch (_) { /* noop */ }
    if (musicEl) try { musicEl.pause(); } catch (_) { /* noop */ }
    if (voEl) try { voEl.pause(); } catch (_) { /* noop */ }
  };
  const draw = () => {
    if (phase === 'main') {
      const t = video.currentTime;
      if (video.ended || (end && t >= end)) {
        if (outroDur > 0) { phase = 'outro'; outroStartTs = performance.now() / 1000; }
        else { finish(); return; }
      } else {
        drawVideoContain(ctx, video, W, H);
        if (groups) drawCaptionFrame(ctx, groups, t, preset, W, H);
        if (overlays.progressBar?.enabled) drawProgressBar(ctx, (t - start) / mainDur, overlays.progressBar.color, W, H);
        if (overlays.lowerThird?.enabled) {
          const lt = overlays.lowerThird;
          if (t >= (lt.start || 0) && t <= (lt.end || mainDur)) drawLowerThird(ctx, lt, W, H);
        }
        drawLogo(ctx, logoImg, overlays.logo, W, H);
      }
    }
    if (phase === 'outro') {
      const elapsed = performance.now() / 1000 - outroStartTs;
      if (elapsed >= outroDur) { finish(); return; }
      drawOutro(ctx, overlays.outro, outroImg, W, H);
      drawLogo(ctx, logoImg, overlays.logo, W, H);
    }
    requestAnimationFrame(draw);
  };
  requestAnimationFrame(draw);
  await done;
  try { await audioCtx.close(); } catch (_) { /* noop */ }
  cleanup();
  return new Blob(chunks, { type: 'video/webm' });
}
